import React from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Switch } from '../../ui/switch';
import { adminApi } from '../../../lib/adminAuth';
import { IconBtn } from './ContractsPanel';
import { errMsg, PLACEHOLDERS } from './docUtils';

function ClauseDialog({ item, sections, onClose, onSaved }) {
  const [f, setF] = React.useState(null);
  React.useEffect(() => { setF(item ? { section_key: sections[0]?.key, title: '', body: '', default_on: true, ...item } : null); }, [item, sections]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { section_key: f.section_key, title: f.title, body: f.body, default_on: f.default_on };
      if (f._id) await adminApi.put(`/api/admin/scope-library/clauses/${f._id}`, body);
      else await adminApi.post('/api/admin/scope-library/clauses', body);
      toast.success('Clause saved'); onSaved();
    } catch (e) { toast.error(errMsg(e, 'Could not save')); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="library-clause-dialog">
        <DialogHeader><DialogTitle>{f._id ? 'Edit clause' : 'New clause'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <select value={f.section_key} onChange={(e) => setF({ ...f, section_key: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="library-clause-section">
            {sections.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Heading (optional), e.g. Confidentiality" data-testid="library-clause-title" />
          <Textarea rows={7} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="font-mono text-xs" placeholder="Clause text" data-testid="library-clause-body" />
          <details className="text-xs text-slate-500"><summary className="cursor-pointer font-semibold">Placeholders you can use</summary>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1 mt-2">{PLACEHOLDERS.map(([k, d]) => <span key={k}><code className="text-fuchsia-700">{k}</code> — {d}</span>)}</div>
          </details>
          {f.key && <p className="text-[11px] text-slate-400">Other clauses can point to this one with <code>{`{{ref:${f.key}}}`}</code></p>}
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.default_on} onCheckedChange={(v) => setF({ ...f, default_on: v })} data-testid="library-clause-default" /> Ticked by default on new contracts</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.body.trim()} className="bg-gray-900 hover:bg-gray-800" data-testid="library-clause-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LibraryClauses({ sections, clauses, canEdit, reload }) {
  const [editing, setEditing] = React.useState(null);
  const [newSection, setNewSection] = React.useState('');
  const call = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e, 'Action failed')); } };
  return (
    <div className="space-y-6" data-testid="library-clauses">
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing({})} data-testid="library-clause-new"><Plus className="w-4 h-4 mr-1" /> Add clause</Button>
          <div className="flex gap-1.5">
            <Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="New section name" className="h-9 w-48" data-testid="library-section-name" />
            <Button size="sm" variant="ghost" disabled={!newSection.trim()} onClick={() => call(() => adminApi.post('/api/admin/scope-library/sections', { name: newSection }), 'Section added').then(() => setNewSection(''))} data-testid="library-section-add">Add section</Button>
          </div>
        </div>
      )}
      {sections.map((s, si) => {
        const rows = clauses.filter((c) => c.section_key === s.key);
        return (
          <div key={s._id} data-testid={`library-section-${s.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-sm font-bold text-slate-800">{si + 2}. {s.name}</p>
              <span className="text-[11px] text-slate-400">{`{{sec:${s.key}}}`}</span>
              {canEdit && <>
                <IconBtn title="Rename section" onClick={() => { const n = window.prompt('Section name', s.name); if (n?.trim()) call(() => adminApi.put(`/api/admin/scope-library/sections/${s._id}`, { name: n }), 'Section renamed'); }} testId={`library-section-rename-${s.key}`}><Pencil /></IconBtn>
                {rows.length === 0 && <IconBtn title="Delete section" danger onClick={() => call(() => adminApi.delete(`/api/admin/scope-library/sections/${s._id}`), 'Section deleted')} testId={`library-section-delete-${s.key}`}><Trash2 /></IconBtn>}
              </>}
            </div>
            <div className="space-y-1.5">
              {rows.map((c) => (
                <div key={c._id} className="flex gap-3 p-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`library-clause-${c.key}`}>
                  <p className="flex-1 text-xs text-slate-600 leading-relaxed whitespace-pre-line">
                    {c.title && <b className="text-slate-800">{c.title}. </b>}{c.body}
                    {!c.default_on && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">optional</span>}
                  </p>
                  {canEdit && <div className="flex gap-0.5 shrink-0">
                    <IconBtn title="Edit" onClick={() => setEditing(c)} testId={`library-clause-edit-${c.key}`}><Pencil /></IconBtn>
                    <IconBtn title="Delete" danger onClick={() => window.confirm('Delete this clause from the library?') && call(() => adminApi.delete(`/api/admin/scope-library/clauses/${c._id}`), 'Clause deleted')} testId={`library-clause-delete-${c.key}`}><Trash2 /></IconBtn>
                  </div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <ClauseDialog item={editing} sections={sections} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
    </div>
  );
}
